/**
 * Throttle helper.
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        // Drawer setup
        this.hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';
        this.drawer = this.querySelector('.sticky-atc-drawer');
        this.drawerVariantContainer = this.querySelector('[data-sticky-drawer-variant-container]');
        this.drawerSubmitBtn = this.querySelector('[data-sticky-drawer-submit]');
        this.drawerPrice = this.querySelector('[data-sticky-drawer-price]');
        this.drawerCompare = this.querySelector('[data-sticky-drawer-compare]');
        this.drawerCloseEls = this.querySelectorAll('[data-sticky-drawer-close]');
        this.drawerInitialized = false;

        if (this.drawer) {
          this.drawerCloseEls.forEach((el) => el.addEventListener('click', this.closeDrawer.bind(this)));
          this.drawerSubmitBtn?.addEventListener('click', this.handleDrawerSubmit.bind(this));
          this.boundEscClose = this.handleEsc.bind(this);
        }

        this.updateImage();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
      }

      /**
       * Submission handler — open drawer if multi-variant, else default behavior.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        // If product has multiple variants, ALWAYS open drawer so customer
        // can confirm/change variant before adding. This eliminates accidental
        // wrong-variant adds on mobile where main picker is off-screen.
        if (this.drawer && !this.hasOnlyDefaultVariant) {
          this.openDrawer();
          return;
        }

        // Default: submit using main form data
        await this.submitWithMainFormData(evt);
      }

      /**
       * Copies form data from main product form, validates, then submits via
       * the parent product-form handleSubmit.
       */
      async submitWithMainFormData(evt) {
        const variantIdInput = this.section.querySelector('product-form buy-buttons input[name="id"]');
        const variantId = variantIdInput ? variantIdInput.value : '';
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            theme.scrollToRevealElement(target);
          }
          return;
        }

        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Opens the variant drawer. On first open, clone variant-picker from main form.
       */
      openDrawer() {
        if (!this.drawerInitialized) {
          this.initDrawerContent();
          this.drawerInitialized = true;
        }
        this.updateDrawerSubmitState();
        this.drawer.classList.add('sticky-atc-drawer--open');
        this.drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('sticky-atc-drawer-open');
        document.addEventListener('keydown', this.boundEscClose);
      }

      closeDrawer() {
        this.drawer?.classList.remove('sticky-atc-drawer--open');
        this.drawer?.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('sticky-atc-drawer-open');
        document.removeEventListener('keydown', this.boundEscClose);
      }

      handleEsc(e) {
        if (e.key === 'Escape') this.closeDrawer();
      }

      /**
       * Pulls the variant-picker (and qty input if present) from the main form,
       * clones it into the drawer, and re-wires it to dispatch real change events
       * on the original main form. This keeps state in sync without re-implementing
       * variant logic.
       */
      initDrawerContent() {
        if (!this.drawerVariantContainer) return;
        const mainPicker = this.section.querySelector('variant-picker');
        if (mainPicker) {
          // Clone the picker's INNER content (not the variant-picker element itself,
          // to avoid duplicate custom-element instances)
          const wrapper = document.createElement('div');
          wrapper.className = 'sticky-atc-drawer__variant-picker';
          wrapper.innerHTML = mainPicker.innerHTML;
          this.drawerVariantContainer.appendChild(wrapper);

          // Wire all input changes in the clone to fire same-name events on main picker
          wrapper.addEventListener('change', (e) => {
            const target = e.target;
            if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
            const mainEl = mainPicker.querySelector(`[name="${CSS.escape(target.name)}"][value="${CSS.escape(target.value)}"]`)
              || mainPicker.querySelector(`[name="${CSS.escape(target.name)}"]`);
            if (mainEl) {
              if (mainEl.type === 'radio' || mainEl.type === 'checkbox') {
                mainEl.checked = true;
              } else {
                mainEl.value = target.value;
              }
              mainEl.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        // Add qty input
        const qtyWrap = document.createElement('div');
        qtyWrap.className = 'sticky-atc-drawer__qty';
        qtyWrap.innerHTML = `
          <label class="sticky-atc-drawer__qty-label">${(window.theme && theme.strings && theme.strings.quantity) || 'Quantity'}</label>
          <div class="sticky-atc-drawer__qty-controls">
            <button type="button" class="sticky-atc-drawer__qty-btn" data-qty-decrease aria-label="Decrease quantity">−</button>
            <input type="number" class="sticky-atc-drawer__qty-input" data-drawer-qty value="1" min="1" inputmode="numeric">
            <button type="button" class="sticky-atc-drawer__qty-btn" data-qty-increase aria-label="Increase quantity">+</button>
          </div>
        `;
        this.drawerVariantContainer.appendChild(qtyWrap);
        const qtyInput = qtyWrap.querySelector('[data-drawer-qty]');
        qtyWrap.querySelector('[data-qty-decrease]').addEventListener('click', () => {
          qtyInput.value = Math.max(1, parseInt(qtyInput.value || '1', 10) - 1);
        });
        qtyWrap.querySelector('[data-qty-increase]').addEventListener('click', () => {
          qtyInput.value = parseInt(qtyInput.value || '1', 10) + 1;
        });
      }

      /**
       * Updates submit button label based on current variant availability.
       */
      updateDrawerSubmitState() {
        if (!this.drawerSubmitBtn) return;
        const variantIdInput = this.section.querySelector('product-form buy-buttons input[name="id"]');
        const variantId = variantIdInput ? variantIdInput.value : '';
        const submitBtn = this.section.querySelector('product-form buy-buttons button[name="add"]');
        const isUnavailable = submitBtn && submitBtn.hasAttribute('disabled');
        if (!variantId || isUnavailable) {
          this.drawerSubmitBtn.disabled = true;
          this.drawerSubmitBtn.textContent = (window.theme && theme.strings && theme.strings.noStock) || 'Unavailable';
        } else {
          this.drawerSubmitBtn.disabled = false;
          this.drawerSubmitBtn.textContent = this.querySelector('button.add-to-cart')?.dataset.addToCartText || 'Add to cart';
        }
      }

      /**
       * Handles ATC click inside drawer: copies main form data + drawer qty,
       * then submits via parent product-form.
       */
      async handleDrawerSubmit() {
        const qtyInput = this.querySelector('[data-drawer-qty]');
        const qty = qtyInput ? parseInt(qtyInput.value || '1', 10) : 1;

        const variantIdInput = this.section.querySelector('product-form buy-buttons input[name="id"]');
        const variantId = variantIdInput ? variantIdInput.value : '';
        if (!variantId) return;

        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid) {
          theme.scrollToRevealElement(this.mainProductForm);
          this.closeDrawer();
          return;
        }

        // Copy variant + qty into sticky form
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        formData.set('id', variantId);
        formData.set('quantity', String(qty));
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          } else if (p[0] === 'quantity' || p[0] === 'id') {
            this.form.querySelector(`[name="${p[0]}"]`).value = p[1];
          }
        }

        const fakeEvent = new Event('submit', { cancelable: true });
        Object.defineProperty(fakeEvent, 'submitter', { value: this.submitBtn });
        const result = super.handleSubmit(fakeEvent);
        this.closeDrawer();
        return result;
      }

      /**
       * Visibility toggle after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          this.variantTitle.textContent = evt.detail.variant.title;
          this.variantContainer.hidden = false;
          if (idInput) idInput.value = evt.detail.variant.id;

          // Update drawer price + compare price
          if (this.drawerPrice && window.theme?.Currency?.formatMoney) {
            this.drawerPrice.textContent = theme.Currency.formatMoney(evt.detail.variant.price);
          } else if (this.drawerPrice) {
            // fallback: use Shopify's formatMoney if available
            this.drawerPrice.textContent = (evt.detail.variant.price / 100).toFixed(2);
          }
          if (this.drawerCompare) {
            if (evt.detail.variant.compare_at_price > evt.detail.variant.price) {
              this.drawerCompare.style.display = '';
            } else {
              this.drawerCompare.style.display = 'none';
            }
          }
        } else {
          this.variantContainer.hidden = true;
          this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant);
        this.updateDrawerSubmitState();
        this.setErrorMsgState();
      }

      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;

        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
